import { fetchClient } from '@/api/typedClient'
import {
  AuthProvider as ExcalibrAuthProvider,
  LoginScreen,
} from '@gravitate-js/excalibrr'
import { type LoginValues } from '@gravitate-js/excalibrr/dist/components/Login/Forms/LoginForm'
import { ValidatePasswordResponse } from '@gravitate-js/excalibrr/dist/components/Login/LoginScreen'
import { message } from 'antd'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

// Configure SSO providers for excalibrr
const ssoConfig = {
  providers: [
    {
      IdentityProviderId: 1,
      Name: 'Google',
      ClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      Provider: 'Google',
      AuthorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      Scopes: 'openid email profile',
      AdditionalParams: {
        access_type: 'offline',
        include_granted_scopes: 'true',
      },
    },
  ],
  authenticateUrl: `${window.location.origin}/api/internal/auth/sso`,
  callbackUrl: `${window.location.origin}/ssoAuth`,
}

export const Login = () => {
  const navigate = useNavigate()
  const { isAuthenticated, login, error } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/integration-management/overview')
    }
  }, [isAuthenticated, navigate])

  async function handleLogin(values: LoginValues) {
    try {
      await login(values.username, values.password)
      navigate('/integration-management/overview')
    } catch (_err) {
      // Error is handled in the context
    }
  }

  // Needs this wrapper to satisfy the validatePasswordEP signature
  async function handleValidatePassword({ password }: { password: string }) {
    const resp = await fetchClient.POST('/auth/check_password', {
      body: password,
    })
    return { data: resp.data as ValidatePasswordResponse }
  }

  async function handleResetPassword(codeAndToken: string, password: string) {
    const [token, code] = codeAndToken.split('::')
    if (!token || !code) {
      console.error('Invalid code and token format')
      return
    }

    const resp = await fetchClient.POST('/meta/user/reset_password', {
      body: {
        password_reset_code: code,
        new_password: password,
      },
      params: {
        query: { token },
        // header: {
        //   Authorization: `Bearer ${token}`,
        // }
      },
    })

    if (resp.error) {
      for (const err of resp.error.detail || []) {
        message.error(`${err.loc.at(-1)} - ${err.msg}`)
      }
      throw new Error('Password reset failed with validation errors')
    }
  }

  async function handleSendResetEmail(email: string) {
    try {
      const { error } = await fetchClient.POST(
        '/meta/user/email_password_reset_link',
        { params: { query: { email } } },
      )

      return !error
    } catch (err) {
      console.error('Send reset email error:', err)
      return false
    }
  }

  return (
    <ExcalibrAuthProvider
      baseUrl={window.location.origin}
      ssoConfig={ssoConfig}
    >
      <LoginScreen
        loginTitle='Welcome to Crossroads'
        loginLayout={2}
        onLogin={handleLogin}
        validateOTP={async (otp: string | null) => {
          // TODO: Implement OTP validation if needed
          console.warn('OTP validation not implemented', { otp })
        }}
        validatePasswordEP={handleValidatePassword}
        // @ts-expect-error TODO: Fix type mismatch in excalibrr. It actually sends (code: string, newPassword: string)
        resetPassword={handleResetPassword}
        sendResetEmail={handleSendResetEmail}
        errorMessage={error || undefined}
      />
    </ExcalibrAuthProvider>
  )
}
